import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../../axiosConfig';

const label = name => ({ admin: 'Administrator', user: 'Utilizator' }[name] || name);
const groupNames = { ADMIN: 'Administrare', ALL: 'Unități', NOM: 'Nomenclatoare', INTRARE: 'Intrări', IESIRE: 'Ieșiri',
  BON: 'Bonuri', NC: 'Contabilitate', RAPOARTE: 'Rapoarte', JURNAL: 'Jurnale', BALANTA: 'Balanță', STOC: 'Stocuri',
  CONTACT: 'Parteneri', COMENZI: 'Comenzi', SANTIER: 'Șantier', SALARII: 'Salarii', SETARI: 'Setări', ISTORIC: 'Istoric' };
const description = p => p.code === 'ALL_BRANCHES' ? 'Acces la toate unitățile firmei' : p.description || p.code;

export default function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState([]);
  const [form, setForm] = useState(null);
  const [baseline, setBaseline] = useState('');
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const dirty = form && JSON.stringify(form) !== baseline;
  const load = async () => {
    const [r, p] = await Promise.all([api.get('/auth/admin/roles'), api.get('/auth/admin/perms')]);
    setRoles(r.data); setPerms(p.data);
  };
  useEffect(() => { load().catch(e => setError(e.response?.data?.detail || e.message)).finally(() => setLoading(false)); }, []);
  useEffect(() => {
    const warn = e => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const open = next => {
    if (dirty && !window.confirm('Renunțați la modificările nesalvate?')) return;
    setForm(next); setBaseline(JSON.stringify(next)); setError(''); setMessage(''); setQuery('');
  };
  const grouped = useMemo(() => {
    const result = {};
    for (const p of perms) {
      if (!`${description(p)} ${p.code}`.toLocaleLowerCase('ro').includes(query.toLocaleLowerCase('ro'))) continue;
      const group = groupNames[p.group || p.code.split('_')[0]] || 'Alte drepturi';
      (result[group] ||= []).push(p);
    }
    return Object.entries(result).sort(([a], [b]) => a.localeCompare(b, 'ro'));
  }, [perms, query]);
  const save = async e => {
    e.preventDefault(); setBusy(true); setError(''); setMessage('');
    try {
      const payload = { name: form.name.trim(), permission_ids: form.permission_ids };
      if (form.id) await api.put(`/auth/admin/roles/${form.id}`, payload);
      else await api.post('/auth/admin/roles', payload);
      await load(); setForm(null); setBaseline('');
      setMessage('Rolul a fost salvat. Drepturile se aplică utilizatorilor cărora le este atribuit, la următoarea autentificare.');
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const remove = async role => {
    if (!window.confirm(`Ștergeți rolul „${role.name}”?`)) return;
    setBusy(true); setError(''); setMessage('');
    try { await api.delete(`/auth/admin/roles/${role.id}`); await load(); setForm(null); setMessage('Rolul a fost șters.'); }
    catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return <div className="container py-3">
    <div className="d-flex justify-content-between align-items-center mb-3">
      <h3>Roluri</h3><Link to="/admin/drepturi" className="btn btn-outline-secondary">Drepturi utilizatori</Link>
    </div>
    <p>Definești un set de drepturi o singură dată și atribui rolul mai multor utilizatori din <strong>Drepturi utilizatori</strong>. Rolurile proprii aparțin firmei curente.</p>
    <div className="alert alert-info">Rolurile predefinite pot fi consultate și copiate. Modificarea copiei afectează doar utilizatorii cărora le atribui noul rol. Excepțiile individuale rămân valabile; un drept interzis explicit rămâne interzis.</div>
    {error && <div role="alert" className="alert alert-danger">{error}</div>}
    {message && <div role="status" className="alert alert-success">{message}</div>}
    <button className="btn btn-primary mb-3" disabled={busy || loading} onClick={() => open({ name: '', permission_ids: [], editable: true })}>Adaugă rol</button>
    {loading ? <p>Se încarcă rolurile…</p> : <table className="table table-striped"><thead><tr><th>Denumire</th><th>Tip</th><th>Drepturi</th><th>Acțiuni</th></tr></thead>
      <tbody>{roles.map(r => <tr key={r.id}><td>{label(r.name)}</td><td>{r.editable ? 'Rol al firmei' : 'Predefinit'}</td><td>{r.permission_ids.length}</td><td className="d-flex gap-2">
        <button className="btn btn-sm btn-outline-primary" disabled={busy} onClick={() => open({ ...r, name: label(r.name) })}>{r.editable ? 'Modifică' : 'Vezi drepturile'}</button>
        <button className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => open({ name: `${label(r.name)} (copie)`, permission_ids: [...r.permission_ids], editable: true })}>Copiază în firmă</button>
        {r.editable && <button className="btn btn-sm btn-outline-danger" disabled={busy} onClick={() => remove(r)}>Șterge</button>}
      </td></tr>)}</tbody></table>}
    {form && <form onSubmit={save} className="card"><div className="card-body">
      <h4>{!form.editable ? 'Drepturile rolului predefinit' : form.id ? 'Modifică rolul' : 'Rol nou'}</h4>
      <label className="form-label" htmlFor="role-name">Denumire rol</label>
      <input id="role-name" className="form-control mb-3" value={form.name} maxLength={100} required disabled={!form.editable || busy} onChange={e => setForm({ ...form, name: e.target.value })} />
      <label className="form-label" htmlFor="role-search">Caută un drept</label>
      <input id="role-search" className="form-control mb-3" value={query} onChange={e => setQuery(e.target.value)} />
      <p>{form.permission_ids.length} drepturi selectate. Salvarea unui rol existent actualizează drepturile tuturor utilizatorilor care îl au.</p>
      {grouped.map(([group, rows]) => <fieldset key={group} className="border rounded p-3 mb-3"><legend className="float-none w-auto fs-6 px-2">{group}</legend>
        {rows.map(p => <div className="form-check" key={p.id}><input className="form-check-input" type="checkbox" id={`role-perm-${p.id}`} disabled={!form.editable || busy}
          checked={form.permission_ids.includes(p.id)} onChange={e => setForm({ ...form, permission_ids: e.target.checked ? [...form.permission_ids, p.id] : form.permission_ids.filter(id => id !== p.id) })} />
          <label className="form-check-label" htmlFor={`role-perm-${p.id}`}>{description(p)}</label></div>)}
      </fieldset>)}
      {form.editable && <button className="btn btn-primary me-2" disabled={busy || !form.name.trim()} type="submit">Salvează rolul</button>}
      <button className="btn btn-outline-secondary" type="button" disabled={busy} onClick={() => open(null)}>Închide</button>
    </div></form>}
  </div>;
}
